import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { profileGaps, PROJECT_TITLE_MAX, type BriefDraft, type ProjectSummary, type ProjectView } from "@joogoos/contracts";
import { api } from "../api.js";
import { Chat } from "./Chat.js";
import { Result, stageOf, type Stage } from "./Result.js";
import { Gallery } from "./stages/Gallery.js";
import { Rail, statusLabel } from "./Rail.js";
import { Week } from "./Week.js";
import { usePref } from "../ui/prefs.js";
import { useProfile, useProjects } from "../ui/reads.js";
import { Icon } from "../ui/icons.js";
import { useNarrow } from "../ui/narrow.js";
import { Toggle } from "../ui/Toggle.js";
import { setOnScreen } from "../reports/onScreen.js";

/** What zone 3 is called on a phone's toggle, by what it holds. */
const PANE: Record<Stage, string> = { ideas: "Ideas", photos: "Photos", voices: "Voice", plan: "Plan", pipeline: "Making it", editor: "Video" };

/**
 * Studio (docs/03). Three zones, all three there from the start on a desk (changed 2026-09-12, at the owner's request).
 *
 * 1. the rail, docked unless the owner collapses it; 2. the chat, always the same column in the same place; 3. the
 * pane, which opens on the gallery and changes stage as the conversation goes — photos, voice, the plan, then the work.
 * Nothing moves sideways when a post is started: it used to collapse the rail and give an empty chat the whole width,
 * and then pull both back when the first thing had to be looked at.
 *
 * On a phone (03 §layout) there is room for one of them. The chat leads, with the gallery under it at the start; the
 * pane is one tap away at the top, with a dot when it has changed since it was last looked at; and the rail is a drawer.
 */
export function Studio({ openId, onOpened, onSeeAll, onBusiness }: {
  /** A post another tab has just made and wants shown — an idea taken off the website, "Make this" (ADR 0035). */
  openId?: string | null;
  onOpened?: (() => void) | undefined;
  onSeeAll?: (() => void) | undefined;
  onBusiness?: (() => void) | undefined;
}) {
  const qc = useQueryClient();
  const [projectId, setProjectId] = useState<string | null>(null);
  // A new name, not `rail.closed`: that one was set to true by every new post until 2026-09-12, so keeping it would have
  // left almost everybody's rail collapsed by the old rule rather than by their own choice.
  const [railClosed, setRailClosed] = usePref("rail.collapsed", false);
  // Looking something up is not a layout change: from the strip the rail floats over the page and goes away again.
  // That state is deliberately not remembered — a peek is a peek.
  const [peeking, setPeeking] = useState(false);
  const [chatClosed, setChatClosed] = usePref("chat.closed", false);
  const [view, setView] = useState<"pipeline" | "editor" | null>(null);
  const narrow = useNarrow();
  const [phonePane, setPhonePane] = useState<"chat" | "pane">("chat");
  const [seen, setSeen] = useState<Stage>("ideas");

  const projects = useProjects();
  /**
   * What the Studio knows about the business, and the one line it says when the answer is "not much".
   *
   * The same read the shell lands on (`App.tsx`) and the same read the Business tab draws, so it costs nothing
   * extra: one cache entry, asked for once. Landing somebody on Business is only the first visit — this is what
   * says so on every visit after it, because a brief written from half a page reads like anybody's brief.
   */
  const profile = useProfile();
  const gaps = profile.data?.available === true ? profileGaps(profile.data.profile) : [];
  // Remembered, because a warning that comes back every time the tab is left and returned to is not a warning any
  // more. It goes for good the moment the page is answered, whether or not it was ever hidden.
  const [hidden, setHidden] = usePref("studio.gaps.hidden", false);
  const project = useQuery({ queryKey: ["project", projectId], queryFn: () => api.project(projectId!), enabled: !!projectId });
  const create = useMutation({
    mutationFn: api.createProject,
    // A new post opens where the last one was: the rail stays as the owner left it, and zone 3 offers the gallery.
    onSuccess: (p) => { setProjectId(p.id); setPeeking(false); void qc.invalidateQueries({ queryKey: ["projects"] }); },
  });
  /**
   * A draft deleted from the rail. It leaves the cached list first, so the next project opened is never the one that is
   * gone; if it was the one on screen, the most recent one left opens instead.
   */
  const remove = useMutation({
    mutationFn: api.deleteProject,
    onSuccess: (_: void, id: string) => {
      const left = (qc.getQueryData<ProjectSummary[]>(["projects"]) ?? []).filter((x) => x.id !== id);
      qc.setQueryData<ProjectSummary[]>(["projects"], left);
      qc.removeQueries({ queryKey: ["project", id] });
      // Chosen here rather than left to the effect that opens the most recent one: the list is asked for again straight
      // after this, and whichever answer lands first must not be able to reopen the draft that was just deleted.
      if (id === projectId) setProjectId(left[0]?.id ?? null);
      void qc.invalidateQueries({ queryKey: ["projects"] });
      // A dropped day of a prepared week was on the calendar too.
      void qc.invalidateQueries({ queryKey: ["calendar"] });
    },
  });
  /**
   * Nothing exists until the first answer: a project is created and answered in one step — by a gallery pick, which is
   * a free answer, or by the first sentence, which is the Studio guide's first turn (ADR 0031).
   */
  const start = useMutation({
    mutationFn: async ({ patch, text }: { patch: BriefDraft | null; text: string }) => {
      const p = await api.createProject();
      return patch ? api.answer(p.id, patch, text) : api.turn(p.id, text);
    },
    onSuccess: (p) => { setProjectId(p.id); qc.setQueryData(["project", p.id], p); },
    // A turn refused at the cap still leaves the project it was said in, so the rail is asked again either way.
    onSettled: () => { void qc.invalidateQueries({ queryKey: ["projects"] }); },
  });
  const onStart = (patch: BriefDraft | null, text: string) => start.mutate({ patch, text });

  /**
   * Live progress, while there is progress to watch (docs/17 §performance 3).
   *
   * The stream is open only while the worker owes this project something. Everything that happens to a project that
   * is chatting is the answer to a request this browser just made and already has; a connection asking the API for a
   * snapshot every second and a half on its behalf — eight queries a time — is work nobody asked for. Pressing
   * Generate moves the status, which opens it; the render finishing moves it back, which closes it.
   */
  const working = project.data?.status === "scripted" || project.data?.status === "generating";
  useEffect(() => {
    if (!projectId || !working) return;
    // The snapshot is the whole project, so the other two lists are re-asked only when something they draw has
    // actually changed: the credits when this project has spent more, the rail when a title or a status moved.
    // Invalidating both on every snapshot cost two extra requests per step of the pipeline.
    let spent: string | undefined;
    let drawn: string | undefined;
    return api.events(projectId, (p: ProjectView) => {
      qc.setQueryData(["project", projectId], p);
      if (p.spentUsd !== spent) { spent = p.spentUsd; void qc.invalidateQueries({ queryKey: ["workspace"] }); }
      const rail = `${p.title}|${p.status}`;
      if (rail !== drawn) { drawn = rail; void qc.invalidateQueries({ queryKey: ["projects"] }); }
    });
  }, [projectId, working, qc]);

  useEffect(() => { if (!projectId && projects.data?.[0]) setProjectId(projects.data[0].id); }, [projects.data, projectId]);
  // Asked for by name, so it wins over both the last project looked at and the newest draft.
  useEffect(() => { if (openId) { setProjectId(openId); onOpened?.(); } }, [openId, onOpened]);
  // The toggle belongs to the project being looked at, not to the tab: carrying "editor" onto a project that has
  // not rendered anything would open a pane with nothing in it.
  // A post started from the Photos button opens on the picker, on a phone too.
  useEffect(() => { setView(null); setPhonePane(projectId && picking?.startsWith(projectId) ? "pane" : "chat"); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  // The project on screen, by id, for a report filed from here (ADR 0024 §3: its id, never its brief).
  useEffect(() => { setOnScreen({ projectId }); return () => setOnScreen({ projectId: null }); }, [projectId]);

  const open = project.data ?? null;
  // The photo picker, opened from the composer's Photos button (2026-09-27): kept open until it is answered, or the
  // post's photos change. Its photos and its look alone were the key, and "No photos" on a Showreel video that had none
  // changed neither, so it never closed (review, 2026-09-29).
  const photosOf = (p: ProjectView) => `${p.id}|${(p.draft.sourceMediaIds ?? []).join(",")}|${p.draft.look ?? ""}`;
  const [picking, setPicking] = useState<string | null>(null);
  const onPhotos = () => {
    if (open) { setPicking(photosOf(open)); setPhonePane("pane"); return; }
    // Nothing started yet: a post made of the owner's photos is started, for nothing, and the picker opens on it.
    start.mutate({ patch: { look: "pictures" }, text: "From my photos" }, { onSuccess: (p) => setPicking(photosOf(p)) });
  };
  const stage = open && open.status === "chatting" && picking === photosOf(open) ? "photos" : stageOf(open, view);
  const ideas = stage === "ideas";
  // Which step has a help page of its own (docs/19): the plan before Generate, and the project being made after it.
  const step = stage === "plan" ? "plan" : stage === "pipeline" || stage === "editor" ? "making" : null;
  useEffect(() => { setOnScreen({ step }); return () => setOnScreen({ step: null }); }, [step]);

  // On a phone the gallery is under the chat, so there is a pane to switch to only once zone 3 holds something else.
  const paned = !ideas && !!open;
  const showPane = narrow && paned && phonePane === "pane";
  useEffect(() => { if (showPane) setSeen(stage); }, [showPane, stage]);

  return (
    <div className="h-full flex flex-col min-h-0">
      {gaps.length && !hidden ? <Gaps gaps={gaps} onFix={onBusiness} onHide={() => setHidden(true)} /> : null}
      <div className="flex-1 flex min-h-0 relative">
        {narrow ? null : (
          <Rail
            mode={railClosed ? "strip" : "docked"}
            projects={projects.data}
            activeId={projectId}
            busy={create.isPending}
            onPick={setProjectId}
            onNew={() => create.mutate()}
            onDelete={(id) => remove.mutate(id)}
            week={<Week projects={projects.data} activeId={projectId} onPick={setProjectId} onDelete={(id) => remove.mutate(id)} />}
            onToggle={() => (railClosed ? setPeeking(true) : setRailClosed(true))}
            onSeeAll={onSeeAll}
          />
        )}
        {peeking ? (
          <>
            <div className={`absolute inset-0 z-40 ${narrow ? "bg-ink/25" : ""}`} onClick={() => setPeeking(false)} />
            <Rail
              mode={narrow ? "drawer" : "overlay"}
              projects={projects.data}
              activeId={projectId}
              busy={create.isPending}
              onPick={(id) => { setProjectId(id); setPeeking(false); }}
              onNew={() => create.mutate()}
              onDelete={(id) => remove.mutate(id)}
              week={<Week projects={projects.data} activeId={projectId} onPick={(id) => { setProjectId(id); setPeeking(false); }} onDelete={(id) => remove.mutate(id)} />}
              onToggle={() => { if (!narrow) setRailClosed(false); setPeeking(false); }}
              onSeeAll={onSeeAll}
            />
          </>
        ) : null}

        {narrow ? (
          <section className="flex-1 min-w-0 bg-panel flex flex-col min-h-0">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-line">
              <button
                onClick={() => setPeeking(true)}
                title="Your posts"
                aria-label="Your posts"
                className="w-8 h-8 shrink-0 grid place-items-center rounded-lg border border-line-2 bg-panel text-muted"
              >
                <Icon name="list" size={16} />
              </button>
              <Name
                project={open}
                sub={<span className="block text-[11px] text-muted truncate">{open ? statusLabel(open.status) : "nothing started"}</span>}
              />
              {paned ? (
                <Toggle
                  value={phonePane}
                  onChange={setPhonePane}
                  options={[["chat", "Chat"], ["pane", PANE[stage]]]}
                  dot={stage !== seen ? "pane" : undefined}
                />
              ) : null}
            </div>
            {open && showPane ? (
              <div className="flex-1 min-h-0 overflow-y-auto">
                <Result project={open} stage={stage} onView={setView} onPicked={() => setPicking(null)} />
              </div>
            ) : (
              <Chat project={open} onStart={onStart} starting={start.isPending} startError={start.error} wide={ideas} onPhotos={onPhotos} />
            )}
          </section>
        ) : chatClosed && !ideas ? (
          <button
            onClick={() => setChatClosed(false)}
            title="Open the chat"
            className="absolute z-20 top-1/2 -translate-y-1/2 bg-ink text-white rounded-r-[14px] px-2 py-4 flex flex-col items-center gap-2.5 shadow-[0_4px_16px_rgba(23,23,26,.2)]"
            style={{ left: railClosed ? 56 : 224 }}
          >
            <Icon name="chat" size={17} />
            <span className="text-[11px] font-semibold uppercase tracking-[.06em] [writing-mode:vertical-rl]">Chat</span>
          </button>
        ) : (
          // The same column in the same place whatever zone 3 holds, so the conversation never jumps sideways.
          <section className="bg-panel flex flex-col min-h-0 w-[420px] border-r border-line shrink-0">
            <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-line">
              <Name project={open} />
              <span className="text-[11.5px] text-muted shrink-0">{open ? statusLabel(open.status) : "nothing started"}</span>
              {ideas ? null : (
                <button onClick={() => setChatClosed(true)} title="Collapse the chat" className="text-faint hover:text-ink">
                  <Icon name="chevronLeft" size={15} />
                </button>
              )}
            </div>
            <Chat project={open} onStart={onStart} starting={start.isPending} startError={start.error} gallery={false} onPhotos={onPhotos} />
          </section>
        )}

        {narrow ? null : (
          <section className="flex-1 min-h-0 overflow-y-auto">
            {ideas || !open
              ? <Gallery project={open} onStart={onStart} starting={start.isPending} />
              : <Result project={open} stage={stage} onView={setView} onPicked={() => setPicking(null)} />}
          </section>
        )}
      </div>
    </div>
  );
}

/**
 * The post's name over the chat, and the way to type another one (docs/03, 2026-09-21).
 *
 * A post is named after its idea, cut at sixty characters, which is a sentence and not a name: a week of posts about
 * the same thing reads as four variations of one line in the rail. So the name here is a button, and the pencil says
 * so; typing over it tells the API this name is the owner's, and the idea stops rewriting it.
 *
 * It lives over the chat rather than in the rail because this is where the name is *read* — the rail's rows are
 * small, already carry a delete, and are for picking, not for editing. `sub` is the phone's second line: the status,
 * which stays under the name whether or not it is being typed over.
 */
function Name({ project, sub }: { project: ProjectView | null; sub?: React.ReactNode }) {
  const qc = useQueryClient();
  const [typed, setTyped] = useState<string | null>(null);
  const rename = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => api.renameProject(id, title),
    // The answer is the whole project, so the open one is replaced outright; the rail is only asked for its list again.
    onSuccess: (p) => { qc.setQueryData(["project", p.id], p); void qc.invalidateQueries({ queryKey: ["projects"] }); setTyped(null); },
  });
  // Another post picked while this one was being renamed: the name on screen is not the one in the box any more.
  const id = project?.id;
  useEffect(() => { setTyped(null); }, [id]);

  const title = project?.title ?? "New post";
  if (typed !== null && project) {
    const ready = typed.trim().length > 0 && !rename.isPending;
    return (
      <span className="min-w-0 flex-1">
        <form
          className="flex items-center gap-1"
          onSubmit={(e) => { e.preventDefault(); if (ready) rename.mutate({ id: project.id, title: typed.trim() }); }}
        >
          <input
            autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} maxLength={PROJECT_TITLE_MAX}
            onKeyDown={(e) => { if (e.key === "Escape") { rename.reset(); setTyped(null); } }}
            aria-label={`A new name for ${title}`}
            className="min-w-0 flex-1 px-2 py-1 rounded-lg border border-line-2 bg-page text-[13px]"
          />
          <button type="submit" disabled={!ready} title="Save the name" aria-label="Save the name" className="shrink-0 text-muted hover:text-ink disabled:opacity-40">
            <Icon name="check" size={15} />
          </button>
          <button type="button" onClick={() => { rename.reset(); setTyped(null); }} title="Keep the old name" aria-label="Keep the old name" className="shrink-0 text-faint hover:text-ink">
            <Icon name="x" size={14} />
          </button>
        </form>
        {rename.error ? <span role="alert" className="block text-[11px] text-bad truncate">{String(rename.error)}</span> : sub}
      </span>
    );
  }

  return (
    <span className="min-w-0 flex-1">
      {project ? (
        <button
          onClick={() => setTyped(project.title)}
          title="Rename this post"
          aria-label={`Rename ${title}`}
          className="group max-w-full flex items-center gap-1.5 text-[13.5px] font-semibold text-left"
        >
          <span className="truncate">{title}</span>
          <Icon name="pencil" size={12} className="shrink-0 text-faint opacity-0 group-hover:opacity-100 group-focus:opacity-100 [@media(hover:none)]:opacity-100" />
        </button>
      ) : (
        <span className="block text-[13.5px] font-semibold truncate">{title}</span>
      )}
      {sub}
    </span>
  );
}

/**
 * The one strip above the Studio: what it does not know about the business, and the way to tell it.
 *
 * It names the missing sections rather than counting them. "Three answers left" is a chore; "it does not know who
 * buys it or how you sound" is the reason the posts will be flat, which is the only argument for going and
 * answering them.
 */
function Gaps({ gaps, onFix, onHide }: { gaps: Array<{ key: string; heading: string }>; onFix?: (() => void) | undefined; onHide: () => void }) {
  const names = gaps.map((g) => g.heading.toLowerCase());
  const said = names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;
  return (
    <div className="shrink-0 flex items-center gap-3 px-3.5 py-2 bg-tint border-b border-line">
      <Icon name="shop" size={15} className="text-coral shrink-0" />
      <p className="text-[12.5px] leading-snug min-w-0">
        <b className="font-semibold">The Studio does not know the business yet.</b>{" "}
        <span className="text-ink-2">Nothing for {said} — every brief is written from that page, so what comes out will read like anybody{"\u2019"}s.</span>
      </p>
      {onFix ? (
        <button onClick={onFix} className="ml-auto shrink-0 px-3 py-1.5 rounded-lg bg-ink text-white text-[11.5px] font-semibold">
          Fill it in
        </button>
      ) : null}
      <button onClick={onHide} title="Not now" aria-label="Hide this" className={`shrink-0 text-faint hover:text-ink ${onFix ? "" : "ml-auto"}`}>
        <Icon name="x" size={14} />
      </button>
    </div>
  );
}
